import {
    Component,
    ojs,
    app,
    state,
    v,
    each,
    payload,
    component,
} from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    isMobile,
    cleanDispose,
    inform,
    updateStateValue,
    consoleErrorOnLocal,
    either,
    monetary,
    getCommissionRatesForPrice,
    dataURLtoBlob,
    appendToFormData,
} from "../../../helpers";
import SolidIcon from "../../Blocks/SolidIcon";
import Spinner, { SmallSpinner } from "../../Blocks/Spinner";
import { api, STRINGS, dc } from "../../../declarations";
import { $e } from "../../../ojs.events";
import Cropper from "cropperjs";
import EditProductImageForm from "../../Forms/EditProductImageForm";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";

const h = app("h");
const router = app("router");
const broker = app("broker");
const tool = app("tool");
const dom = app("dom");

ojs(SolidIcon, EditProductImageForm, Spinner, SmallSpinner);

export default class EditService extends Component {
    businessId = null;
    serviceId = null;
    priceState = state("");
    form = state({
        name: "",
        description: "",
        price: "",
        discount_price: "",
        customer_instruction: "",
        deliverer_instruction: "",
        requirements: [],
        work_type: "",
        processing_time: "",
    });
    loading = state(true);
    saving = state(false);
    thumbnail = state(null); // { original, cropped, cropData }
    shouldAutoGenerateImage = state(false);
    cropper = { obj: null };
    currentStep = state(1);
    requirements = state([]);
    editingRequirementIndex = state(-1);
    currentRequirement = state(null);
    requirementHolder = null;

    availableAtAllBranches = state(true);
    selectedBranches = state([]);
    branches = state([]);
    loadingBranches = state(false);

    nextStep() {
        if (this.currentStep.value < 6) {
            this.currentStep.value++;
            scrollToTop();
        }
    }

    prevStep() {
        if (this.currentStep.value > 1) {
            this.currentStep.value--;
            scrollToTop();
        }
    }

    async fetchBranches() {
        this.loadingBranches.value = true;
        try {
            const res = await api.get(`companies/${this.businessId}/branches`);
            if (res) {
                this.branches.value = res.data.branches;
            }
        } catch (e) {
            consoleErrorOnLocal(e);
        } finally {
            this.loadingBranches.value = false;
        }
    }

    toggleBranch(id) {
        updateStateValue(this.selectedBranches, (v) => {
            if (v.includes(id)) {
                return v.filter((bid) => bid !== id);
            }
            v.push(id);
            return v;
        });
    }

    triggerUpload(id) {
        document.getElementById(id).click();
    }

    handleImageUpload(e) {
        const files = e.target.files;
        if (!files.length) return;

        const file = files[0];
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.addEventListener("load", () => {
            const src = fileReader.result;
            const options = new tool.ModalOptions();
            options.buttons.right.text = "Crop & Confirm";
            options.buttons.left.text = "Cancel";
            options.classes.buttons.right = "btn btn-primary rounded-pill px-4";
            options.classes.dialog = "modal-lg modal-fullscreen-md-down";
            options.staticBackdrop();

            const { buttons, modal } = tool.modal(
                "Crop Service Image",
                h.EditProductImageForm(src),
                options,
            );

            modal.element.addEventListener("shown.bs.modal", () => {
                const preview = dom.id("epif-image-view");
                preview.classList.remove("d-none");

                this.cropper.obj = new Cropper(preview, {
                    aspectRatio: 16 / 9,
                    viewMode: 2,
                    minContainerHeight: 350,
                });
            });

            buttons.left.button.addEventListener("click", () => modal.close());

            buttons.right.button.addEventListener("click", () => {
                const cropData = this.cropper.obj.getData();
                const croppedSrc = this.cropper.obj
                    .getCroppedCanvas()
                    .toDataURL();

                this.thumbnail.value = {
                    original: src,
                    cropped: croppedSrc,
                    cropData: cropData,
                };
                this.shouldAutoGenerateImage.value = false;

                modal.close();
            });
        });
    }

    $$service = {
        updated: (ed, event) => {
            this.saving.value = false;
            router.to("business.services.manage", {
                companyId: this.businessId,
            });
        },
        has: {
            updateError: (ed, event) => {
                this.saving.value = false;
            },
        },
    };

    async init() {
        this.businessId = router.params.companyId;
        this.serviceId = router.params.serviceId;
        await this.fetchService();
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (c) c.init();
    }

    async fetchService() {
        this.loading.value = true;
        try {
            const res = await api.get(
                `companies/${this.businessId}/services/${this.serviceId}`,
            );
            const s = res.data.service;

            this.form.value = {
                name: s.name,
                description: s.description || "",
                price: s.price,
                discount_price: s.discount_price || "",
                customer_instruction: s.customer_instruction || "",
                deliverer_instruction: s.deliverer_instruction || "",
                requirements: Array.isArray(s.requirements)
                    ? s.requirements
                    : [],
                work_type: s.work_type || "",
                processing_time: s.processing_time || "",
            };

            this.priceState.value = s.price;

            this.requirements.value = (
                Array.isArray(s.requirements) ? s.requirements : []
            ).map((req) => {
                return {
                    id: req.id || Date.now() + Math.random(),
                    name: req.name,
                    choice_type: req.choice_type,
                    is_required: !!req.is_required,
                    allow_multiple: !!req.allow_multiple,
                    price: req.price || 0,
                    overrides_base_price: !!req.overrides_base_price,
                    values: (Array.isArray(req.choices) ? req.choices : []).map(
                        (c) => ({
                            value: c.value,
                            price: c.price || 0,
                        }),
                    ),
                };
            });

            this.shouldAutoGenerateImage.value = s.autogenerate_image || false;
            this.thumbnail.value = s.thumbnail
                ? {
                      original: dc.simg(s.thumbnail),
                      cropped: dc.simg(s.thumbnail),
                  }
                : null;
            this.availableAtAllBranches.value =
                s.available_at_all_branches ?? true;
            this.selectedBranches.value = s.branches
                ? s.branches.map((b) => b.id)
                : [];
        } catch (e) {
            console.error(e);
            inform("Failed to load service", STRINGS.ERROR);
        } finally {
            this.loading.value = false;
        }
    }

    addRequirement() {
        this.form.value.requirements.push({
            id: Date.now(),
            name: "",
            type: "text",
            required: false,
            multi_entry: false,
            options: "", // comma separated for selects
        });
        this.form.value = { ...this.form.value }; // trigger reactivity
    }

    removeRequirement(id) {
        this.form.value.requirements = this.form.value.requirements.filter(
            (r) => r.id !== id,
        );
        this.form.value = { ...this.form.value };
    }

    async saveService(e) {
        if (e) e.preventDefault();

        // 1. Basic Info Validation
        const name = this.form.value.name?.trim();
        const price = this.form.value.price;
        const description = this.form.value.description?.trim();
        const customerInstruction =
            this.form.value.customer_instruction?.trim();
        const delivererInstruction =
            this.form.value.deliverer_instruction?.trim();
        const workType = this.form.value.work_type;
        const processingTime = this.form.value.processing_time;

        if (!name) {
            inform("Service name is required", STRINGS.ERROR);
            this.currentStep.value = 1;
            return;
        }
        if (!price || parseFloat(price) < 0) {
            inform(
                "Service price must be a valid positive number",
                STRINGS.ERROR,
            );
            this.currentStep.value = 1;
            return;
        }
        if (!description) {
            inform("Service description is required", STRINGS.ERROR);
            this.currentStep.value = 1;
            return;
        }

        // 2. Requirements validation
        const requirementsList = this.requirements.value || [];
        for (let i = 0; i < requirementsList.length; i++) {
            const req = requirementsList[i];
            if (!req.name?.trim()) {
                inform(`Requirement #${i + 1} must have a name`, STRINGS.ERROR);
                this.currentStep.value = 3;
                return;
            }
            if (
                req.choice_type === "single_choice" ||
                req.choice_type === "multiple_choice"
            ) {
                const values = req.values || [];
                if (values.length === 0) {
                    inform(
                        `Requirement "${req.name}" must have at least one option`,
                        STRINGS.ERROR,
                    );
                    this.currentStep.value = 3;
                    return;
                }
                for (let j = 0; j < values.length; j++) {
                    if (!values[j].value?.trim()) {
                        inform(
                            `All options in requirement "${req.name}" must have a name`,
                            STRINGS.ERROR,
                        );
                        this.currentStep.value = 3;
                        return;
                    }
                }
            }
        }

        // 3. Instructions Validation
        if (!customerInstruction) {
            inform("Customer instructions are required", STRINGS.ERROR);
            this.currentStep.value = 4;
            return;
        }
        if (!delivererInstruction) {
            inform("Deliverer instructions are required", STRINGS.ERROR);
            this.currentStep.value = 4;
            return;
        }

        // 4. Workflow & Processing Validation
        if (!workType) {
            inform("Work type is required", STRINGS.ERROR);
            this.currentStep.value = 5;
            return;
        }
        if (!processingTime) {
            inform("Processing time is required", STRINGS.ERROR);
            this.currentStep.value = 5;
            return;
        }

        // 5. Branches Validation
        if (
            !this.availableAtAllBranches.value &&
            this.selectedBranches.value.length === 0
        ) {
            inform(
                "Please select at least one branch or enable availability for all branches",
                STRINGS.ERROR,
            );
            this.currentStep.value = 6;
            return;
        }

        this.saving.value = true;

        try {
            const payload = {
                name: this.form.value.name,
                description: this.form.value.description,
                price: this.form.value.price,
                discount_price: this.form.value.discount_price,
                customer_instruction: this.form.value.customer_instruction,
                deliverer_instruction: this.form.value.deliverer_instruction,
                work_type: this.form.value.work_type,
                processing_time: this.form.value.processing_time,
                _method: "PUT", // For Laravel put method via POST
            };

            // Wait, if no new crop, don't send thumbnail as new upload
            if (this.thumbnail.value && this.thumbnail.value.cropData) {
                payload.thumbnail = {
                    original: await dataURLtoBlob(this.thumbnail.value.original),
                    cropped: await dataURLtoBlob(this.thumbnail.value.cropped),
                    cropData: this.thumbnail.value.cropData,
                };
                payload.autogenerate_image = this.shouldAutoGenerateImage.value;
                payload.should_auto_generate_image =
                    this.shouldAutoGenerateImage.value;
            } else if (this.thumbnail.value) {
                payload.thumbnail = this.thumbnail.value.cropped;
                payload.autogenerate_image = this.shouldAutoGenerateImage.value;
                payload.should_auto_generate_image =
                    this.shouldAutoGenerateImage.value;
            } else {
                payload.thumbnail = "";
                payload.autogenerate_image = true;
                payload.should_auto_generate_image = true;
            }

            payload.requirements = this.requirements.value;
            payload.available_at_all_branches =
                this.availableAtAllBranches.value;
            payload.branches = this.availableAtAllBranches.value
                ? []
                : this.selectedBranches.value;

            payload.organization = {
                id: window.currentCompany.id,
                type: "Company",
            };

            const formData = new FormData();
            appendToFormData(formData, payload);

            const res = await api.post(
                `companies/${this.businessId}/services/${this.serviceId}`,
                formData,
                {
                    fetch_using: "browser",
                    "remove-content-type": true,
                }
            );
            if (res) {
                inform("Service updated successfully", STRINGS.SUCCESS);
                this.saving.value = false;
                router.to("business.services.manage", {
                    companyId: this.businessId,
                });
            }
        } catch (err) {
            inform(
                err.response?.data?.message || "Failed to update service",
                STRINGS.ERROR,
            );
            this.saving.value = false;
        }
    }

    render() {
        pageInfo("Edit Service");
        scrollToTop();

        return h.div(
            { class: "min-vh-100 py-5 mb-5 bg-light py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    { class: "mb-5 d-flex align-items-center gap-3" },
                    h.button(
                        {
                            class: "btn btn-white border shadow-sm rounded-circle p-2",
                            listeners: { click: () => history.back() },
                        },
                        h.SolidIcon("arrow-left"),
                    ),
                    h.h2({ class: "fw-bold mb-0" }, "Edit Service"),
                ),

                v(this.loading, (l) => {
                    if (l.value) {
                        return h.div(
                            { class: "text-center py-5" },
                            h.div({ class: "spinner-border text-primary" }),
                        );
                    }

                    return h.div(
                        { class: "row justify-content-center" },
                        h.div(
                            { class: "col-lg-8" },
                            this.renderProgress(),
                            h.form(
                                {
                                    class: "card border-0 shadow-sm rounded-4 overflow-hidden slide-in-from-right",
                                    style: "min-height: 500px;",
                                    listeners: {
                                        submit: this.method("saveService"),
                                    },
                                },
                                h.div(
                                    { class: "card-body p-4 p-md-5" },
                                    v(this.currentStep, (step) => {
                                        switch (step.value) {
                                            case 1:
                                                return this.renderStep1();
                                            case 2:
                                                return this.renderStep2();
                                            case 3:
                                                return this.renderStep3();
                                            case 4:
                                                return this.renderStep4();
                                            case 5:
                                                return this.renderStep5();
                                            case 6:
                                                return this.renderStep6();
                                            default:
                                                return null;
                                        }
                                    }),
                                ),
                            ),
                        ),
                    );
                }),
            ),
            either(this.thumbnail, (thumb) =>
                thumb && thumb.original && !thumb.cropped
                    ? EditProductImageForm({
                          image: thumb.original,
                          aspectRatio: 16 / 9,
                          onSave: (croppedImage, cropData) => {
                              this.thumbnail.value = {
                                  ...thumb,
                                  cropped: croppedImage,
                                  cropData: cropData,
                              };
                          },
                          onCancel: () => {
                              this.thumbnail.value = null;
                              document.getElementById("thumbnail-input").value =
                                  "";
                          },
                      })
                    : h.empty(),
            ),
        );
    }

    renderProgress() {
        return h.div(
            { class: "mb-3 text-center" },
            h.div(
                {
                    class: "d-flex justify-content-center align-items-center gap-2 gap-md-3 mb-3",
                },
                [1, 2, 3, 4, 5, 6].map((i) =>
                    v(this.currentStep, (step) =>
                        h.div(
                            {
                                class: `rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all ${
                                    step.value === i
                                        ? "bg-primary text-white shadow"
                                        : step.value > i
                                        ? "bg-success text-white"
                                        : "bg-white text-muted border"
                                }`,
                                style: "width: 40px; height: 40px;",
                            },
                            step.value > i
                                ? h.i({ class: "bi bi-check-lg" })
                                : i,
                        ),
                    ),
                ),
            ),
            v(this.currentStep, (step) =>
                h.h6(
                    { class: "text-muted text-uppercase fw-bold small ls-1" },
                    `Step ${step.value} of 5`,
                ),
            ),
        );
    }

    renderStep1() {
        return h.div(
            { class: "animate-step" },
            h.h3({ class: "fw-bold mb-2" }, "Basic Information"),
            h.p(
                { class: "text-muted mb-5" },
                "Update the core details of your service.",
            ),
            h.div(
                { class: "row g-4 mb-5" },
                h.div(
                    { class: "col-md-6" },
                    h.label({ class: "form-label fw-bold" }, "Service Name"),
                    h.input({
                        type: "text",
                        class: "form-control form-control-lg",
                        required: true,
                        value: this.form.value.name,
                        listeners: {
                            input: (e) =>
                                (this.form.value.name = e.target.value),
                        },
                    }),
                ),
                h.div(
                    { class: "col-md-3" },
                    h.label({ class: "form-label fw-bold" }, "Price"),
                    h.p(
                        { class: "text-muted small mb-2" },
                        "The standard price for this service.",
                    ),
                    h.input({
                        type: "number",
                        class: "form-control form-control-lg",
                        required: true,
                        min: "0",
                        value: this.priceState.value,
                        listeners: {
                            input: (e) => {
                                this.form.value.price = e.target.value;
                                this.priceState.value = e.target.value;
                            },
                        },
                    }),
                ),

                v(this.priceState, (priceStateVal) => {
                    const basePrice = parseFloat(priceStateVal.value) || 0;
                    if (basePrice <= 0) return null;

                    const { customerRate, businessRate } =
                        getCommissionRatesForPrice(basePrice);

                    const finalPrice =
                        basePrice + Math.ceil((basePrice * customerRate) / 100);
                    const payout =
                        basePrice - Math.ceil((basePrice * businessRate) / 100);

                    return h.div(
                        { class: "col-12 mt-4" },
                        h.div(
                            {
                                class: "row g-3 flex-nowrap overflow-x-auto pb-2 flex-md-wrap",
                            },
                            // Customer Card: Final Adjusted Price
                            h.div(
                                { class: "col-10 col-md-6 flex-shrink-0" },
                                h.div(
                                    {
                                        class: "card border-0 shadow-sm p-3 text-center h-100",
                                        style: "border-radius: 16px;",
                                    },
                                    h.i({
                                        class: "bi bi-person-fill text-warning fs-3 mb-1",
                                    }),
                                    h.div(
                                        {
                                            class: "text-muted small fw-semibold",
                                        },
                                        "Final Adjusted Price",
                                    ),
                                    h.div(
                                        {
                                            class: "small text-muted mb-2",
                                            style: "font-size: 0.75rem;",
                                        },
                                        "Price customers will see",
                                    ),
                                    h.div(
                                        {
                                            class: "fw-bold fs-4 text-dark mb-1",
                                        },
                                        monetary(finalPrice, dc.userCurrency),
                                    ),
                                    h.span(
                                        {
                                            class: "bg-warning-subtle text-warning-emphasis rounded-pill px-2 py-1 fs-xsm rounded",
                                        },
                                        `+${customerRate}% Price Markup`,
                                    ),
                                ),
                            ),
                            // Business Card: Receive after Carata Commission
                            h.div(
                                { class: "col-10 col-md-6 flex-shrink-0" },
                                h.div(
                                    {
                                        class: "card border-0 shadow-sm p-3 text-center h-100",
                                        style: "border-radius: 16px;",
                                    },
                                    h.i({
                                        class: "bi bi-briefcase-fill text-primary fs-3 mb-1",
                                    }),
                                    h.div(
                                        {
                                            class: "text-muted small fw-semibold",
                                        },
                                        "You'll Receive",
                                    ),
                                    h.div(
                                        {
                                            class: "small text-muted mb-2",
                                            style: "font-size: 0.75rem;",
                                        },
                                        "Amount you'll receive on Purchase",
                                    ),
                                    h.div(
                                        {
                                            class: "fw-bold fs-4 text-dark mb-1",
                                        },
                                        monetary(payout, dc.userCurrency),
                                    ),
                                    h.span(
                                        {
                                            class: "bg-primary-subtle text-primary-emphasis rounded-pill px-2 py-1 fs-xsm rounded",
                                        },
                                        `-${businessRate}% Carata Commission`,
                                    ),
                                ),
                            ),
                        ),
                    );
                }),
                h.div(
                    { class: "col-md-3" },
                    h.label(
                        { class: "form-label fw-bold" },
                        "Discount Price (Optional)",
                    ),
                    h.p(
                        { class: "text-muted small mb-2" },
                        "A discounted price, if applicable.",
                    ),
                    h.input({
                        type: "number",
                        class: "form-control form-control-lg",
                        min: "0",
                        value: this.form.value.discount_price,
                        listeners: {
                            input: (e) =>
                                (this.form.value.discount_price =
                                    e.target.value),
                        },
                    }),
                ),
                h.div(
                    { class: "col-12" },
                    h.label({ class: "form-label fw-bold" }, "Description"),
                    h.textarea(
                        {
                            class: "form-control",
                            rows: "4",
                            listeners: {
                                input: (e) =>
                                    (this.form.value.description =
                                        e.target.value),
                            },
                        },
                        this.form.value.description,
                    ),
                ),
            ),
            h.div(
                { class: "d-flex justify-content-end mt-5 pt-3" },
                h.button(
                    {
                        type: "button",
                        class: "btn btn-primary rounded-pill px-4",
                        onclick: this.method("nextStep"),
                    },
                    "Next Step",
                    h.i({ class: "bi bi-arrow-right ms-2" }),
                ),
            ),
        );
    }

    renderStep2() {
        return h.div(
            { class: "animate-step" },
            h.h3({ class: "fw-bold mb-2" }, "Instructions"),
            h.p(
                { class: "text-muted mb-5" },
                "Set expectations and guidance for customers and staff.",
            ),
            h.div(
                { class: "row g-4 mb-5" },
                h.div(
                    { class: "col-12" },
                    h.label(
                        { class: "form-label fw-bold" },
                        "Customer Instructions",
                    ),
                    h.p(
                        { class: "text-muted small mb-2" },
                        "Instructions shown to the customer when requesting this service.",
                    ),
                    h.textarea(
                        {
                            class: "form-control",
                            rows: "4",
                            listeners: {
                                input: (e) =>
                                    (this.form.value.customer_instruction =
                                        e.target.value),
                            },
                        },
                        this.form.value.customer_instruction,
                    ),
                ),
                h.div(
                    { class: "col-12" },
                    h.label(
                        { class: "form-label fw-bold" },
                        "Deliverer Instructions",
                    ),
                    h.p(
                        { class: "text-muted small mb-2" },
                        "Instructions shown to the Carata deliverer assigned to this service.",
                    ),
                    h.textarea(
                        {
                            class: "form-control",
                            rows: "4",
                            listeners: {
                                input: (e) =>
                                    (this.form.value.deliverer_instruction =
                                        e.target.value),
                            },
                        },
                        this.form.value.deliverer_instruction,
                    ),
                ),
            ),
            this.renderNavigationButtons(),
        );
    }

    renderStep3() {
        return h.div(
            { class: "animate-step" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.div(
                    h.h3({ class: "fw-bold mb-1" }, "Service Requirements"),
                    h.p(
                        { class: "text-muted mb-0" },
                        "What do you need from the customer to perform this service?",
                    ),
                ),
            ),

            v(this.requirements, (reqs) =>
                h.div(
                    { class: "d-grid gap-4 mb-5" },
                    reqs.value.length === 0
                        ? h.div(
                              {
                                  class: "p-5 bg-light rounded-4 text-center text-muted border-dashed",
                              },
                              "Click 'Add Requirement' to define what you need.",
                          )
                        : reqs.value.map((r, i) =>
                              this.renderRequirementItem(r, i),
                          ),
                ),
            ),

            h.div(
                { class: "d-flex justify-content-end" },
                h.button(
                    {
                        class: "align-items-center btn btn-outline-primary d-flex justify-content-center px-3 rounded-pill w-100",
                        listeners: {
                            click: () => this.openRequirementForm(),
                        },
                    },
                    h.i({ class: "bi bi-plus-lg me-2" }),
                    "Add Requirement",
                ),
            ),

            this.renderNavigationButtons(),
        );
    }

    renderStep4() {
        return h.div(
            { class: "animate-step" },
            h.h3({ class: "fw-bold mb-2" }, "Service Image"),
            h.p(
                { class: "text-muted mb-5" },
                "Upload a high-quality image that represents your service.",
            ),

            h.div(
                { class: "mb-5 text-center" },
                v(this.thumbnail, (thumb) => {
                    const hasImage =
                        thumb.value &&
                        (thumb.value.cropped || thumb.value.original);
                    const imgSrc = thumb.value
                        ? thumb.value.cropped || thumb.value.original
                        : null;
                    return h.div(
                        {
                            class: `position-relative d-inline-block rounded-4 overflow-hidden shadow-sm ${
                                !hasImage ? "bg-light border-dashed w-100" : ""
                            }`,
                            style: hasImage
                                ? "width: 250px; height: 180px;"
                                : "height: 200px;",
                        },
                        hasImage
                            ? h.img({
                                  src: imgSrc,
                                  class: "w-100 h-100 object-fit-cover",
                                  alt: "Service Image",
                              })
                            : h.div(
                                  {
                                      class: "d-flex flex-column justify-content-center align-items-center h-100 p-4",
                                  },
                                  h.div(
                                      {
                                          class: "bg-white p-3 rounded-circle shadow-sm mb-3",
                                      },
                                      h.SolidIcon("image"),
                                  ),
                                  h.span(
                                      { class: "fw-bold text-dark" },
                                      "No Image Selected",
                                  ),
                                  h.span(
                                      { class: "text-muted small mt-1" },
                                      "Upload one below.",
                                  ),
                              ),
                        hasImage
                            ? h.button(
                                  {
                                      type: "button",
                                      class: "btn btn-danger btn-sm position-absolute top-0 end-0 m-2 rounded-circle shadow",
                                      style: "width: 32px; height: 32px; padding: 0;",
                                      listeners: {
                                          click: () =>
                                              (this.thumbnail.value = null),
                                      },
                                  },
                                  h.i({ class: "bi bi-x-lg" }),
                              )
                            : null,
                    );
                }),
            ),

            h.div(
                { class: "d-flex justify-content-center gap-3 mb-4" },
                h.input({
                    type: "file",
                    id: "service-image-upload",
                    class: "d-none",
                    accept: "image/*",
                    listeners: { change: (e) => this.handleImageUpload(e) },
                }),
                h.button(
                    {
                        type: "button",
                        class: "btn btn-outline-primary rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center",
                        listeners: {
                            click: () =>
                                this.triggerUpload("service-image-upload"),
                        },
                    },
                    h.i({ class: "bi bi-cloud-arrow-up me-2" }),
                    "Upload Image",
                ),
            ),

            h.div(
                {
                    class: "form-check form-switch mt-5 d-flex align-items-center gap-2",
                },
                h.input({
                    class: "form-check-input mt-0",
                    type: "checkbox",
                    role: "switch",
                    id: "auto-gen-toggle",
                    checked: this.shouldAutoGenerateImage.value,
                    listeners: {
                        change: (e) =>
                            (this.shouldAutoGenerateImage.value =
                                e.target.checked),
                    },
                }),
                h.label(
                    {
                        class: "form-check-label small fw-bold text-muted text-uppercase",
                        for: "auto-gen-toggle",
                    },
                    "Auto-generate Image using AI if none is provided",
                ),
            ),

            this.renderNavigationButtons(),
        );
    }

    renderSelect(name, label, options, help = "") {
        return h.div(
            { class: "mb-4" },
            h.label({ class: "form-label fw-bold mb-1" }, label),
            h.select(
                {
                    class: "form-select bg-light border p-3 rounded-3",
                    listeners: {
                        change: (e) => (this.form.value[name] = e.target.value),
                    },
                },
                h.option({ value: "" }, "Select..."),
                ...options.map((opt) =>
                    h.option(
                        {
                            value: opt.value,
                        },
                        opt.label,
                        either(this.form.value[name] == opt.value, {
                            selected: true,
                        }),
                    ),
                ),
            ),
            help ? h.small({ class: "text-muted mt-1 d-block" }, help) : null,
        );
    }

    renderStep5() {
        return h.div(
            { class: "animate-step" },
            h.h3({ class: "fw-bold mb-2" }, "Workflow & Processing"),
            h.p(
                { class: "text-muted mb-4" },
                "How does this service operate and how long does it take?",
            ),

            this.renderSelect(
                "processing_time",
                "Processing Time (Minutes)",
                Array.from({ length: 12 }, (_, i) => {
                    const val = (i + 1) * 5;
                    return { value: val, label: val + " mins" };
                }),
                "Estimated time to complete this service (optional).",
            ),

            h.h6({ class: "fw-bold mt-4 mb-3" }, "Work Type"),
            v(this.form, (form) =>
                h.div(
                    { class: "row g-3" },
                    [
                        {
                            id: "customer_pickup_dropoff",
                            title: "Pick-up and Drop-off (Customer to Business)",
                            icon: "box-arrow-in-right",
                            desc: "Carata picks up an item from the customer and drops it off at your business location.",
                        },
                        {
                            id: "business_pickup_dropoff",
                            title: "Pick-up & Drop-off (Business to Customer)",
                            icon: "truck",
                            desc: "Carata picks up an item from your business location and delivers it to the customer.",
                        },
                        {
                            id: "customer_pickup_return",
                            title: "Pick-up & Return (Customer)",
                            icon: "arrow-repeat",
                            desc: "Carata picks up an item from the customer, drops it off at your business, and returns it back to the customer after service is completed.",
                        },
                        {
                            id: "business_pickup_return",
                            title: "Pick-up & Return (Business)",
                            icon: "house-door",
                            desc: "Carata picks up an item from your business location, drops it off at the customer's location, and returns it to you after service is completed.",
                        },
                    ].map((wt) =>
                        h.div(
                            { class: "col-md-6" },
                            h.div(
                                {
                                    class: `card h-100 cursor-pointer transition-all border ${
                                        this.form.value.work_type === wt.id
                                            ? "border-primary bg-primary bg-opacity-10 shadow-sm"
                                            : "border-light bg-white hover-shadow"
                                    }`,
                                    listeners: {
                                        click: () => {
                                            updateStateValue(form, (value) => {
                                                value.work_type = wt.id;
                                                return value;
                                            });
                                        },
                                    },
                                },
                                h.div(
                                    {
                                        class: "card-body d-flex align-items-start p-4",
                                    },
                                    h.div(
                                        {
                                            class: `circle p-2 me-3 d-flex align-items-center justify-content-center ${
                                                this.form.value.work_type ===
                                                wt.id
                                                    ? "bg-primary text-white"
                                                    : "bg-light text-muted"
                                            }`,
                                            style: "width: 48px; height: 48px;",
                                        },
                                        h.i({ class: `bi bi-${wt.icon} fs-4` }),
                                    ),
                                    h.div(
                                        {},
                                        h.h6(
                                            {
                                                class: `fw-bold mb-1 ${
                                                    this.form.value
                                                        .work_type === wt.id
                                                        ? "text-primary"
                                                        : "text-dark"
                                                }`,
                                            },
                                            wt.title,
                                        ),
                                        h.p(
                                            { class: "text-muted small mb-0" },
                                            wt.desc,
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),
            ),

            h.div(
                { class: "text-center mt-3 mb-4" },
                v(this.form, (form) =>
                    h.button(
                        {
                            class: "btn btn-link text-danger btn-sm text-decoration-none",
                            listeners: {
                                click: () => {
                                    updateStateValue(form, (value) => {
                                        value.work_type = "";
                                        return value;
                                    });
                                },
                            },
                        },
                        "Clear Selection",
                    ),
                ),
            ),

            this.renderNavigationButtons(),
        );
    }

    renderStep6() {
        return v(this.availableAtAllBranches, (all) => {
            if (all.value) {
                return h.div(
                    { class: "text-center py-5 animate-step" },
                    h.div(
                        {
                            class: "bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex p-4 mb-4",
                        },
                        h.SolidIcon("shop"),
                    ),
                    h.h3({ class: "fw-bold mb-2" }, "Available Everywhere"),
                    h.p(
                        { class: "text-muted mb-4 px-md-5 mx-md-5" },
                        "This service will be available at all current and future branches of your business.",
                    ),
                    h.button(
                        {
                            class: "btn btn-outline-primary rounded-pill px-4 fw-bold",
                            listeners: {
                                click: () =>
                                    (this.availableAtAllBranches.value = false),
                            },
                        },
                        "Select Specific Branches Instead",
                    ),
                    this.renderNavigationButtons(true),
                );
            }

            return h.div(
                { class: "animate-step" },
                h.div(
                    {
                        class: "d-flex justify-content-between align-items-center mb-4",
                    },
                    h.h3({ class: "fw-bold mb-0" }, "Select Branches"),
                    h.button(
                        {
                            class: "btn btn-light btn-sm rounded-pill fw-bold",
                            listeners: {
                                click: () =>
                                    (this.availableAtAllBranches.value = true),
                            },
                        },
                        "Available at All",
                    ),
                ),
                h.p(
                    { class: "text-muted mb-4" },
                    "Choose which locations offer this service.",
                ),

                v(this.loadingBranches, (loading) => {
                    if (loading.value) {
                        return h.div(
                            { class: "text-center py-5" },
                            Spinner("primary"),
                            h.p(
                                { class: "text-muted mt-3 small fw-bold" },
                                "LOADING BRANCHES...",
                            ),
                        );
                    }

                    return v(this.branches, (branches) => {
                        if (branches.value.length === 0) {
                            return h.div(
                                {
                                    class: "bg-light p-5 rounded-4 text-center border-dashed",
                                },
                                h.h5(
                                    { class: "text-muted mb-0" },
                                    "No branches found.",
                                ),
                            );
                        }

                        return h.div(
                            { class: "row g-3" },
                            v(this.selectedBranches, (selected) => {
                                return branches.value.map((branch) => {
                                    const isSelected = selected.value.includes(
                                        branch.id,
                                    );
                                    return h.div(
                                        { class: "col-md-6" },
                                        h.div(
                                            {
                                                class: `card h-100 cursor-pointer transition-all border ${
                                                    isSelected
                                                        ? "border-primary bg-primary bg-opacity-10 shadow-sm"
                                                        : "border-light bg-white hover-shadow"
                                                }`,
                                                listeners: {
                                                    click: () =>
                                                        this.toggleBranch(
                                                            branch.id,
                                                        ),
                                                },
                                            },
                                            h.div(
                                                {
                                                    class: "card-body d-flex align-items-center p-4",
                                                },
                                                h.div(
                                                    {
                                                        class: `rounded-circle p-2 me-3 d-flex align-items-center justify-content-center ${
                                                            isSelected
                                                                ? "bg-primary text-white"
                                                                : "bg-light text-muted"
                                                        }`,
                                                        style: "width: 48px; height: 48px;",
                                                    },
                                                    h.i({
                                                        class: `bi bi-shop fs-4`,
                                                    }),
                                                ),
                                                h.div(
                                                    {},
                                                    h.h6(
                                                        {
                                                            class: `fw-bold mb-1 ${
                                                                isSelected
                                                                    ? "text-primary"
                                                                    : "text-dark"
                                                            }`,
                                                        },
                                                        branch.name,
                                                    ),
                                                    h.p(
                                                        {
                                                            class: "text-muted small mb-0",
                                                        },
                                                        branch.address ||
                                                            "No address provided",
                                                    ),
                                                ),
                                                isSelected
                                                    ? h.div(
                                                          {
                                                              class: "ms-auto text-primary",
                                                          },
                                                          h.i({
                                                              class: "bi bi-check-circle-fill fs-4",
                                                          }),
                                                      )
                                                    : null,
                                            ),
                                        ),
                                    );
                                });
                            }),
                        );
                    });
                }),
                this.renderNavigationButtons(true),
            );
        });
    }

    renderRequirementItem(requirement, index) {
        return h.div(
            {
                class: "card border-0 shadow-none bg-light p-4",
                style: "border-radius: 16px;",
            },
            h.div(
                { class: "d-flex justify-content-between align-items-start" },
                h.div(
                    { class: "flex-fill" },
                    h.h6({ class: "fw-bold mb-1" }, requirement.name),
                    h.div(
                        { class: "text-muted small" },
                        h.span(
                            { class: "badge bg-white text-dark border me-2" },
                            requirement.choice_type.replace("_", " "),
                        ),
                        either(
                            requirement.is_required,
                            () =>
                                h.span(
                                    { class: "text-danger me-2" },
                                    "Required",
                                ),
                            () => h.span({ class: "me-2" }, "Optional"),
                        ),
                        either(
                            requirement.overrides_base_price,
                            () =>
                                h.span(
                                    {
                                        class: "badge bg-warning text-dark me-2",
                                    },
                                    "Overrides Base Price",
                                ),
                            () => "",
                        ),
                        either(
                            requirement.price > 0,
                            () =>
                                h.span(
                                    { class: "text-success fw-bold" },
                                    `+${dc.userCurrency} ${requirement.price}`,
                                ),
                            () => "",
                        ),
                    ),
                    either(
                        requirement.values &&
                            requirement.values.length > 0 &&
                            ["single_choice", "multiple_choice"].includes(
                                requirement.choice_type,
                            ),
                        () =>
                            h.div(
                                { class: "mt-3 d-flex flex-wrap gap-2" },
                                requirement.values.map((v) =>
                                    h.span(
                                        {
                                            class: "badge bg-white text-dark border p-2",
                                        },
                                        v.value,
                                        v.price > 0
                                            ? h.span(
                                                  {
                                                      class: "text-success ms-1",
                                                  },
                                                  `(+${dc.userCurrency} ${v.price})`,
                                              )
                                            : "",
                                    ),
                                ),
                            ),
                        () => null,
                    ),
                ),
                h.div(
                    { class: "d-flex gap-2 ms-3" },
                    h.button(
                        {
                            class: "btn btn-white btn-sm rounded-circle border shadow-sm text-primary",
                            listeners: {
                                click: () => this.openRequirementForm(index),
                            },
                        },
                        h.i({ class: "bi bi-pencil" }),
                    ),
                    h.button(
                        {
                            class: "btn btn-white btn-sm rounded-circle border shadow-sm text-danger",
                            listeners: {
                                click: () => {
                                    let current = this.requirements.value;
                                    current.splice(index, 1);
                                    this.requirements.value = current;
                                },
                            },
                        },
                        h.i({ class: "bi bi-trash" }),
                    ),
                ),
            ),
        );
    }

    renderNavigationButtons(isLastStep = false) {
        return h.div(
            { class: "d-flex justify-content-between align-items-center mt-5" },
            h.button(
                {
                    type: "button",
                    class: "btn btn-white border rounded-pill px-4 fw-bold d-flex align-items-center",
                    listeners: { click: () => this.prevStep() },
                },
                h.i({ class: "bi bi-arrow-left me-2" }),
                "Back",
            ),
            v(this.loading, (l) => {
                if (isLastStep) {
                    return h.button(
                        {
                            type: "button",
                            class: "btn btn-success rounded-pill px-4 fw-bold shadow-sm align-items-center d-flex",
                            listeners: { click: () => this.saveService() },
                        },
                        either(
                            l.value,
                            [
                                { disabled: true },
                                h.span({
                                    class: "spinner-border spinner-border-sm me-2",
                                    role: "status",
                                    "aria-hidden": "true",
                                }),
                                "Saving...",
                            ],
                            () => [
                                "Save Service",
                                h.i({ class: "bi bi-check2 ms-2" }),
                            ],
                        ),
                    );
                }
                return h.button(
                    {
                        type: "button",
                        class: "btn btn-primary rounded-pill px-4 fw-bold shadow-sm align-items-center d-flex",
                        listeners: { click: () => this.nextStep() },
                    },
                    "Continue",
                    h.i({ class: "bi bi-arrow-right ms-2" }),
                );
            }),
        );
    }

    openRequirementForm(index = -1) {
        if (this.requirementHolder) {
            cleanDispose(this.requirementHolder);
        }

        let requirement = this.requirements.value[index] ?? null;

        this.editingRequirementIndex.value = index;
        this.currentRequirement.value = requirement
            ? tool.deepCopy(requirement)
            : {
                  name: "",
                  choice_type: "text",
                  is_required: false,
                  price: 0,
                  overrides_base_price: false,
                  allow_multiple: false,
                  values: [], // { value, price }
              };

        const form = () => this.renderRequirementForm();

        if (isMobile()) {
            this.requirementHolder = tool.offcanvas(h.OffcanvasBody(form()), {
                class: "rounded-top-5",
            });
        } else {
            this.requirementHolder = tool.plainModal(h.ModalBody(form()));
        }

        this.requirementHolder.show();
    }

    renderRequirementForm() {
        return h.div(
            { class: "p-2" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h4(
                    { class: "fw-bold mb-0" },
                    v(this.editingRequirementIndex, (i) =>
                        i.value === -1 ? "New Requirement" : "Edit Requirement",
                    ),
                ),
                h.button({
                    class: "btn-close",
                    listeners: { click: () => this.requirementHolder.hide() },
                }),
            ),
            v(this.currentRequirement, (req) => {
                if (!req.value) return null;
                const r = req.value;

                return h.div(
                    {},
                    h.div(
                        { class: "mb-3" },
                        h.label(
                            {
                                class: "form-label fw-bold small text-muted mb-1",
                            },
                            "Requirement Name",
                        ),
                        h.input({
                            class: "form-control bg-light border p-2 rounded-3",
                            value: r.name,
                            placeholder: "e.g. Size, Format, Material",
                            listeners: {
                                input: (e) => {
                                    updateStateValue(req, (value) => {
                                        value.name = e.target.value;
                                        return value;
                                    });
                                },
                            },
                        }),
                    ),
                    h.div(
                        { class: "row mb-3" },
                        h.div(
                            { class: "col-6" },
                            h.label(
                                {
                                    class: "form-label fw-bold small text-muted mb-1",
                                },
                                "Type",
                            ),
                            h.select(
                                {
                                    class: "form-select bg-light border p-2 rounded-3",
                                    listeners: {
                                        change: (e) => {
                                            updateStateValue(req, (value) => {
                                                value.choice_type =
                                                    e.target.value;
                                                return value;
                                            });
                                        },
                                    },
                                },
                                h.option(
                                    {
                                        value: "file",
                                        selected: r.choice_type === "file",
                                    },
                                    "File Upload",
                                ),
                                h.option(
                                    {
                                        value: "text",
                                        selected: r.choice_type === "text",
                                    },
                                    "Text Input",
                                ),
                                h.option(
                                    {
                                        value: "number",
                                        selected: r.choice_type === "number",
                                    },
                                    "Number Input",
                                ),
                                h.option(
                                    {
                                        value: "boolean",
                                        selected: r.choice_type === "boolean",
                                    },
                                    "Yes/No (Boolean)",
                                ),
                                h.option(
                                    {
                                        value: "single_choice",
                                        selected:
                                            r.choice_type === "single_choice",
                                    },
                                    "Single Choice",
                                ),
                                h.option(
                                    {
                                        value: "multiple_choice",
                                        selected:
                                            r.choice_type === "multiple_choice",
                                    },
                                    "Multiple Choice",
                                ),
                            ),
                        ),
                        either(
                            ["number", "boolean"].includes(r.choice_type),
                            () =>
                                h.div(
                                    { class: "col-6" },
                                    h.label(
                                        {
                                            class: "form-label fw-bold small text-muted mb-1",
                                        },
                                        "Price Modifier",
                                    ),
                                    h.input({
                                        class: "form-control bg-light border p-2 rounded-3",
                                        type: "number",
                                        value: r.price,
                                        placeholder: "0.00",
                                        listeners: {
                                            input: (e) => {
                                                updateStateValue(
                                                    req,
                                                    (value) => {
                                                        value.price =
                                                            e.target.value;
                                                        return value;
                                                    },
                                                );
                                            },
                                        },
                                    }),
                                ),
                            () => h.empty(),
                        ),
                    ),
                    h.div(
                        { class: "d-flex gap-4 mb-4 flex-wrap" },
                        h.div(
                            { class: "form-check form-switch" },
                            h.input({
                                class: "form-check-input",
                                type: "checkbox",
                                checked: r.is_required,
                                listeners: {
                                    change: (e) => {
                                        updateStateValue(req, (value) => {
                                            value.is_required =
                                                e.target.checked;
                                            return value;
                                        });
                                    },
                                },
                            }),
                            h.label(
                                { class: "form-check-label small" },
                                "Required?",
                            ),
                        ),
                        either(
                            ["number", "boolean", "single_choice"].includes(
                                r.choice_type,
                            ),
                            () =>
                                h.div(
                                    { class: "form-check form-switch" },
                                    h.input({
                                        class: "form-check-input",
                                        type: "checkbox",
                                        checked: r.overrides_base_price,
                                        listeners: {
                                            change: (e) => {
                                                updateStateValue(
                                                    req,
                                                    (value) => {
                                                        value.overrides_base_price =
                                                            e.target.checked;
                                                        return value;
                                                    },
                                                );
                                            },
                                        },
                                    }),
                                    h.label(
                                        { class: "form-check-label small" },
                                        "Overrides Base Price?",
                                    ),
                                ),
                            () => h.empty(),
                        ),
                        either(
                            ["file", "text"].includes(r.choice_type),
                            () =>
                                h.div(
                                    { class: "form-check form-switch" },
                                    h.input({
                                        class: "form-check-input",
                                        type: "checkbox",
                                        checked: r.allow_multiple,
                                        listeners: {
                                            change: (e) => {
                                                updateStateValue(
                                                    req,
                                                    (value) => {
                                                        value.allow_multiple =
                                                            e.target.checked;
                                                        return value;
                                                    },
                                                );
                                            },
                                        },
                                    }),
                                    h.label(
                                        { class: "form-check-label small" },
                                        "Allow Multiple?",
                                    ),
                                ),
                            () => h.empty(),
                        ),
                    ),
                    either(
                        ["single_choice", "multiple_choice"].includes(
                            r.choice_type,
                        ),
                        () =>
                            h.div(
                                { class: "bg-white border rounded-3 p-3 mb-3" },
                                h.h6(
                                    { class: "fw-bold small text-muted mb-3" },
                                    "Options",
                                ),
                                ...(r.values || []).map((v, i) =>
                                    h.div(
                                        { class: "row g-2 mb-2" },
                                        h.div(
                                            {
                                                class:
                                                    r.choice_type ===
                                                    "single_choice"
                                                        ? "col-7"
                                                        : "col-10",
                                            },
                                            h.input({
                                                class: "form-control form-control-sm rounded-3",
                                                placeholder: "Option Name",
                                                value: v.value,
                                                listeners: {
                                                    input: (e) => {
                                                        updateStateValue(
                                                            req,
                                                            (value) => {
                                                                value.values[
                                                                    i
                                                                ].value =
                                                                    e.target.value;
                                                                return value;
                                                            },
                                                        );
                                                    },
                                                },
                                            }),
                                        ),
                                        either(
                                            r.choice_type === "single_choice",
                                            () =>
                                                h.div(
                                                    { class: "col-3" },
                                                    h.input({
                                                        class: "form-control form-control-sm rounded-3",
                                                        placeholder: "+ Price",
                                                        type: "number",
                                                        value: v.price,
                                                        listeners: {
                                                            input: (e) => {
                                                                updateStateValue(
                                                                    req,
                                                                    (value) => {
                                                                        value.values[
                                                                            i
                                                                        ].price =
                                                                            e.target.value;
                                                                        return value;
                                                                    },
                                                                );
                                                            },
                                                        },
                                                    }),
                                                ),
                                            () => h.empty(),
                                        ),
                                        h.div(
                                            { class: "col-2 text-end" },
                                            h.button(
                                                {
                                                    class: "btn btn-light btn-sm text-danger rounded-3",
                                                    listeners: {
                                                        click: () => {
                                                            updateStateValue(
                                                                req,
                                                                (value) => {
                                                                    value.values.splice(
                                                                        i,
                                                                        1,
                                                                    );
                                                                    return value;
                                                                },
                                                            );
                                                        },
                                                    },
                                                },
                                                h.i({ class: "bi bi-x-lg" }),
                                            ),
                                        ),
                                    ),
                                ),
                                h.button(
                                    {
                                        class: "btn btn-outline-secondary btn-sm w-100 mt-2 rounded-3",
                                        listeners: {
                                            click: () => {
                                                updateStateValue(
                                                    req,
                                                    (value) => {
                                                        value.values ??= [];
                                                        value.values.push({
                                                            value: "",
                                                            price: 0,
                                                        });
                                                        return value;
                                                    },
                                                );
                                            },
                                        },
                                    },
                                    "+ Add Option",
                                ),
                            ),
                        () => null,
                    ),
                    h.button(
                        {
                            class: "btn btn-primary w-100 fw-bold rounded-pill",
                            listeners: {
                                click: () => this.saveRequirement(),
                            },
                        },
                        "Save Requirement",
                    ),
                );
            }),
        );
    }
    saveRequirement() {
        const req = this.currentRequirement.value;
        if (!req.name.trim())
            return inform("Please enter a requirement name", "warning");

        const index = this.editingRequirementIndex.value;
        let current = this.requirements.value;
        current ??= [];

        if (index === -1) {
            current.push(req);
        } else {
            current[index] = req;
        }

        this.requirements.value = current;

        this.requirementHolder.hide();
    }
}

ojs(EditService);
